// Import Packages
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Import Material UI
import { Select, MenuItem } from '@material-ui/core';

// Import Pages
import Table from '../../../FlowConfigurationInputs/Table/Table';
import { TextInput } from '../../../FlowConfigurationInputs/TextInput/NewTextInput';
import { CredentialPicker } from '../../../FlowConfigurationInputs/CredentialPicker/CredentialPicker';
import FieldLabel from '../../../FlowConfigurationInputs/FieldLabel/FieldLabel';
import AppCredential from "../AppCredential/AppCredential";
import AppCredentialCustomOAuth from "../AppCredentialCustomOAuth/AppCredentialCustomOAuth";
import VariablePicker from '../../../FlowConfigurationInputs/VariablePicker/VariablePicker';

// Import Recoil
import { useSetRecoilState, useRecoilValue, useRecoilState } from 'recoil';
import { saveAvailable, initialActions, changedFields, activeAutom } from "../../../../recoil-store/recoil-state";

// Import Helpers
import { CheckIfFlowUpdated } from '../../../../helpers/helper';

function DynamicList({ action, variables, setOpenVariable, automId, showAlert, automVersionId, getVariables }) {
    const { t } = useTranslation();
    const label = 'CONTENT.CONFIGURATION-FORM.RESTFUL-WEB-SERVICE.';
    const [credVerify, setCredVerify] = useState('none');
    const [content, setContent] = useState('urlencoded');
    const [method, setMethod] = useState('get');
    const [outLabel, setOutLabel] = useState({ isOk: false, value: "" });
    const [automDetail, setAutomDetail] = useRecoilState(activeAutom);

    const initialActionsData = useRecoilValue(initialActions);
    const setSaveAvailable = useSetRecoilState(saveAvailable);
    const [changedFieldsState, setChangeFields] = useRecoilState(changedFields);
    const headers = [label + 'NAME', label + 'VALUE'];
    const options = {
        tableHeader: { headers, title: label + 'HEADER-FIELD' },
        tableBody: { headers, title: label + 'QUERY-PARAMS' },
        tableBodyParamBody: { headers, title: label + 'BODY-PARAMS' },

    };

    useEffect(() => {
        if (!action.fields.authentication) {
            action.fields.authentication = { value: 'none', isRequired: false };
        } else {
            setCredVerify(action.fields.authentication.value);
        }
        if (!action.fields.method) {
            action.fields.method = { value: 'get', isRequired: false };
        } else {
            setMethod(action.fields.method.value);
        }
        if (!action.fields.contentType) {
            action.fields.contentType = { value: 'urlencoded', isRequired: true };
        } else {
            setContent(action.fields.contentType.value);
        }
    }, [action]);

    useEffect(() => {
        if (action.fields?.label) {
            setOutLabel({ isOk: true, value: action.fields.label.value })
        }
    }, [action?.fields?.label?.value])

    const updateLabel = (id, items) => {
        let isDifferentLabel
        const innerUpdateLabel = (id, items) => {
            items.forEach(item => {
                if (item.id === id) {
                    item.fields = action.fields;
                    if (item.label !== action.fields.label.value && (item.label !== undefined || action.fields.label.value)) {
                        item.label = action.fields.label.value;
                        isDifferentLabel = true
                    }
                } else if (item.children) {
                    innerUpdateLabel(id, item.children);
                }
            });
        }
        innerUpdateLabel(id, items)
        return isDifferentLabel
    };

    const handleBlur = () => {
        let fields = action.fields;
        let items = JSON.parse(JSON.stringify(automDetail.items));
        action.fields = JSON.parse(JSON.stringify(fields));

        const isLabelsDifferent = updateLabel(action.id, items);
        if (!isLabelsDifferent) return
        setAutomDetail({ ...automDetail, items: JSON.parse(JSON.stringify(items)) });
    };

    const handleContentSelect = (event) => {
        let value = event.target.value;
        if (value === 'urlencoded' || value === 'form-data') {
            action.fields.bodyParams = { type: 'TABLE', value: [] };
        } else {
            action.fields.bodyParams = { value: '' };
        }
        action.fields.contentType.value = value;
        setContent(value);
    }

    const handleSelect = (event) => {
        let value = event.target.value;
        if (value !== 'basic') {
            delete action.fields.loginUser;
            delete action.fields.loginPassword;
        }
        const field = initialActionsData[action.id]?.fields.authentication;
        const oldValue = !field ? undefined : field.value;
        CheckIfFlowUpdated('authentication', action, oldValue, value, setSaveAvailable, setChangeFields, changedFieldsState);
        action.fields.authentication.value = value;
        setCredVerify(value);
    }

    const handleSelectMethod = (event) => {
        const value = event.target.value;

        const field = initialActionsData[action.id]?.fields.method;
        const oldValue = !field ? undefined : field.value;
        CheckIfFlowUpdated('method', action, oldValue, value, setSaveAvailable, setChangeFields, changedFieldsState);

        action.fields.method.value = value;
        setMethod(value);
    }

    return (
        <div>
            <AppCredential action={action} />
            <div className="form-element">
                {t("CONTENT.CONFIGURATION-FORM.WHATSAPP.TEXT.LABEL")}
                <TextInput
                    action={action}
                    inputName="label"
                    variables={variables}
                    setOpenVariable={setOpenVariable}
                    isRequired={false}
                    onBlur={handleBlur}
                    fieldType="string"
                    displayName={t(label + "LABEL")}
                    outNewValue={outLabel}
                    limit={40}
                />
            </div>
            <div className="conf-field-container">
                <FieldLabel name={t(label + 'REQUEST-URI')} is_required />
                <TextInput action={action} inputName="uriAddress" variables={variables}
                    setOpenVariable={setOpenVariable} isRequired fieldType="string"
                    displayName={t(label + 'REQUEST-URI')} hideRequired />
            </div>
            <div className="conf-field-container">
                <FieldLabel name={t(label + 'RESPONSE-TITLE-PATH')} is_required />
                <TextInput action={action} inputName="responseTitlePath" variables={variables}
                    setOpenVariable={setOpenVariable} isRequired fieldType="string"
                    displayName={t(label + 'RESPONSE-TITLE-PATH')} hideRequired />
                <small>{t(label + 'RESPONSE-TITLE-PATH-HELP')}</small>
            </div>
            <div className="conf-field-container">
                <FieldLabel name={t(label + 'RESPONSE-DESCRIPTION-PATH')} />
                <TextInput action={action} inputName="responseDescriptionPath" variables={variables}
                    setOpenVariable={setOpenVariable} fieldType="string"
                    displayName={t(label + 'RESPONSE-DESCRIPTION-PATH')} hideRequired />
                <small
                    className="conf-field-desc"><em>{t(label + 'DESCRIPTION-HELPER', { type: 'string' })}</em></small>
            </div>
            <div className="conf-field-container">
                <FieldLabel name={t(label + 'AUTHENTICATION')} />
                <Select value={credVerify} onChange={handleSelect} variant="outlined" fullWidth style={{ width: "90%" }}>
                    <MenuItem value="none">{t(label + 'NO-AUTH')}</MenuItem>
                    <MenuItem value="basic">{t(label + 'BASIC-AUTH')}</MenuItem>
                    <MenuItem value="oauth20">{t(label + 'OAUTH20-AUTH')}</MenuItem>
                </Select>
            </div>
            {credVerify === 'basic' &&
                <div>
                    <div className="conf-field-container">
                        <FieldLabel name={t(label + 'USERNAME')} is_required />
                        <CredentialPicker action={action} inputName="loginUser" variables={variables}
                            setOpenVariable={setOpenVariable} isRequired={true} hideRequired />
                    </div>
                    <div className="conf-field-container">
                        <FieldLabel name={t(label + 'PASSWORD')} is_required />
                        <CredentialPicker action={action} inputName="loginPassword" variables={variables}
                            setOpenVariable={setOpenVariable} isPassword={true} isRequired={true}
                            hideRequired />
                    </div>
                </div>
            }
            {credVerify === 'oauth20' &&
                <div>
                    <AppCredentialCustomOAuth action={action} />
                </div>
            }
            <div className="conf-field-container">
                <FieldLabel name={t(label + 'METHOD')} />
                <Select value={method} onChange={handleSelectMethod} variant="outlined" fullWidth
                    style={{ width: "90%" }}>
                    <MenuItem value="get">{t(label + 'GET')}</MenuItem>
                    <MenuItem value="post">{t(label + 'POST')}</MenuItem>
                </Select>
            </div>
            {method === "post" && (
                <div className="conf-field-container">
                    <FieldLabel name="Content Type" />
                    <Select value={content} onChange={handleContentSelect} variant="outlined" fullWidth
                        style={{ width: "90%" }}>
                        <MenuItem value="urlencoded">urlencoded</MenuItem>
                        <MenuItem value="form-data">form-data</MenuItem>
                        <MenuItem value="application/json">application/json</MenuItem>
                        <MenuItem value="application/xml">application/xml</MenuItem>
                        <MenuItem value="text/plain">text/plain</MenuItem>
                        <MenuItem value="text/xml">text/xml</MenuItem>
                        <MenuItem value="text/html">text/html</MenuItem>
                    </Select>
                </div>
            )}
            {method === "post" && (
                <div className="form-element">
                    {content === 'urlencoded' || content === 'form-data' ?
                        <Table action={action} inputName="bodyParams" automVersionId={automVersionId}
                            options={options.tableBodyParamBody}
                            isRequired={false} setOpenVariable={setOpenVariable}
                            formData={content === 'form-data'} />
                        :
                        <TextInput action={action} inputName="bodyParams" variables={variables}
                            setOpenVariable={setOpenVariable} multiline rows={4}
                            fieldType={content === 'application/json' ? 'object' : 'string'}
                            displayName={t(label + 'BODY-PARAMS')} />
                    }
                </div>
            )}
            <div className="form-element">
                <Table action={action} inputName="headerParams" automVersionId={automVersionId}
                    options={options.tableHeader}
                    setOpenVariable={setOpenVariable} isRequired={false} />
            </div>
            <div className="form-element">
                <Table action={action} inputName="queryParams" automVersionId={automVersionId}
                    options={options.tableBody}
                    setOpenVariable={setOpenVariable} />
            </div>
            <div className="form-element">
                {t(label + 'RESPONSE')}
                <VariablePicker action={action} inputName="set"
                    variables={variables}
                    disabled={action.fields["set"] && action.fields["set"].value}
                    setOpenVariable={setOpenVariable}
                    styles={{ backgroundColor: "white" }}
                    filter="string"
                    isOutput={true}
                />
                <small>ex: {`{ "title": "title text", "description": "desctription text" }`}</small>
                {/* <TextInput action={action} inputName="set" variables={variables} disabled={true}
                    setOpenVariable={setOpenVariable} rows={4} fieldType="string"
                    displayName={t(label + 'RESPONSE')} /> */}
            </div>
        </div>
    )
}

export default DynamicList;
